import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import {
  type ApprovalListItem,
  useApprovalTouchpointAnchor,
} from "../lib/approvals";
import { useAddManualEvent } from "../lib/customers";
import {
  computeApprovalInsertTimestamp,
  type ApprovalTouchpointKind,
} from "../lib/approval-insert-position";
import { ApiError } from "../lib/api";
import {
  Field,
  FIELD_CLASS,
  INSERT_ERROR_MESSAGES,
  MetaCampaignFields,
  type CampaignAttribution,
} from "./InsertTouchpointMark";

/**
 * "Touchpoint einfügen" in the approval drawer, right under the origin block
 * (TASK-76, Epic A3/A4/A6) — exactly two options, deliberately no free
 * position like the journey's insertion marks (issue: "keine dritte
 * Option"). Never rendered for merge_review gates (caller's job) and never
 * blocks Genehmigen/Ablehnen — this section is fully self-contained and
 * doesn't touch the drawer's approve/reject state.
 */
export function ApprovalTouchpointInsert({
  item,
  open,
  dialogContainer,
}: {
  item: ApprovalListItem;
  open: boolean;
  /** EventDetailDrawer's `Dialog.Content` DOM node (TASK-80), forwarded to
   *  MetaCampaignFields so its Combobox option lists portal inside the
   *  modal dialog instead of `document.body` — a modal Radix dialog
   *  disables pointer events everywhere outside its own content node, so a
   *  `document.body` portal is unclickable here. */
  dialogContainer: HTMLDivElement | null;
}) {
  const anchor = useApprovalTouchpointAnchor(item.id, open);
  const [kind, setKind] = useState<ApprovalTouchpointKind | null>(null);

  // Review F3: reset when the drawer moves to a different approval (j/k
  // navigation while it stays open, EventDetailDrawer stays the same
  // instance). Same reset-on-item-id pattern as EventDetailDrawer's
  // droppedKeys/showTech, applied one level deeper — that's where this
  // "which contact" form state actually lives.
  useEffect(() => {
    setKind(null);
  }, [item.id]);

  return (
    <div className="mt-6 rounded-md border border-border-primary bg-bg-secondary px-4 py-3">
      <p className="mb-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
        Touchpoint einfügen
      </p>
      {anchor.isLoading ? (
        <p className="text-xs text-text-tertiary">Lädt…</p>
      ) : anchor.isError || !anchor.data ? (
        // Review F2: request itself failed (network/HTTP) — distinct from a
        // successful lookup that just couldn't resolve a contact below.
        <p className="text-xs text-text-tertiary">
          Anker für „Touchpoint einfügen" konnte nicht geladen werden —
          Genehmigen/Ablehnen bleibt möglich.
        </p>
      ) : anchor.data.visitorId === null ? (
        <p className="text-xs text-text-tertiary">
          Dieser Kontakt lässt sich nicht sicher zuordnen (keine passende
          E-Mail-Kennung gefunden) — Touchpoint kann hier nicht eingefügt
          werden.
        </p>
      ) : (
        <>
          {/* Review F1: the mirror check ran but failed — "First Touch"
              relies on oldestKnownAt covering BOTH sources, so warn (never
              block, same as the journey's unloaded-older-events hint)
              whenever that option is in play. Irrelevant to "Mittel-Touch",
              which never reads oldestKnownAt. */}
          {anchor.data.bqMirrorEnabled &&
            anchor.data.error &&
            kind !== "middle" && (
              <div className="mb-2 flex items-start gap-2 rounded-md border border-warning bg-warning-bg/40 px-3 py-2 text-xs text-text-primary">
                <AlertTriangle
                  size={14}
                  className="mt-0.5 shrink-0 text-warning"
                />
                <div className="flex-1">
                  Langzeitspeicher konnte gerade nicht geprüft werden — der
                  Anfang lässt sich hier evtl. nicht sicher bestimmen.
                </div>
              </div>
            )}
          {kind === null ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setKind("middle")}
                className="flex flex-1 flex-col items-center rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm font-medium text-text-primary transition-colors hover:border-accent hover:text-accent"
              >
                Mittel-Touch
                <span className="text-[11px] font-normal text-text-tertiary">
                  unmittelbar vor diesem Ereignis
                </span>
              </button>
              <button
                type="button"
                onClick={() => setKind("first")}
                className="flex flex-1 flex-col items-center rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm font-medium text-text-primary transition-colors hover:border-accent hover:text-accent"
              >
                First Touch
                <span className="text-[11px] font-normal text-text-tertiary">
                  vor dem ältesten bekannten Ereignis
                </span>
              </button>
            </div>
          ) : (
            <ApprovalTouchpointForm
              projectId={item.projectId}
              visitorId={anchor.data.visitorId}
              kind={kind}
              approvalAt={item.createdAt}
              oldestKnownAt={anchor.data.oldestKnownAt}
              onClose={() => setKind(null)}
              dialogContainer={dialogContainer}
            />
          )}
        </>
      )}
    </div>
  );
}

function ApprovalTouchpointForm({
  projectId,
  visitorId,
  kind,
  approvalAt,
  oldestKnownAt,
  onClose,
  dialogContainer,
}: {
  projectId: number;
  visitorId: number;
  kind: ApprovalTouchpointKind;
  approvalAt: string;
  oldestKnownAt: string | null;
  onClose: () => void;
  dialogContainer: HTMLDivElement | null;
}) {
  const computed = computeApprovalInsertTimestamp(
    kind,
    approvalAt,
    oldestKnownAt,
  );
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [campaignAttribution, setCampaignAttribution] =
    useState<CampaignAttribution>({ utmCampaign: "" });
  const add = useAddManualEvent(projectId);

  const canSubmit = utmSource.trim().length > 0 && !add.isPending;

  const submit = () => {
    if (!canSubmit) return;
    add.mutate(
      {
        visitorId,
        // TASK-151: PageView, not Interaction (TASK-76/TASK-59's original
        // choice) — same event type the journey's insert form uses.
        eventType: "PageView",
        createdAt: computed,
        utmSource: utmSource.trim(),
        ...(utmMedium.trim() ? { utmMedium: utmMedium.trim() } : {}),
        ...(campaignAttribution.utmCampaign.trim()
          ? { utmCampaign: campaignAttribution.utmCampaign.trim() }
          : {}),
        ...(campaignAttribution.utmContent?.trim()
          ? { utmContent: campaignAttribution.utmContent.trim() }
          : {}),
        ...(campaignAttribution.utmTerm?.trim()
          ? { utmTerm: campaignAttribution.utmTerm.trim() }
          : {}),
        ...(campaignAttribution.dhC ? { dhC: campaignAttribution.dhC } : {}),
        ...(campaignAttribution.dhAs ? { dhAs: campaignAttribution.dhAs } : {}),
        ...(campaignAttribution.dhA ? { dhA: campaignAttribution.dhA } : {}),
      },
      {
        onSuccess: () => {
          toast.success(
            "Touchpoint in die Pipeline geschrieben — erscheint in wenigen Sekunden in der Journey.",
          );
          onClose();
        },
        onError: (err) => {
          const code = (
            (err as ApiError)?.body as { error?: string } | undefined
          )?.error;
          toast.error(
            (code && INSERT_ERROR_MESSAGES[code]) ??
              `Einfügen fehlgeschlagen: ${(err as Error).message}`,
          );
        },
      },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-text-primary">
          {kind === "middle" ? "Mittel-Touch" : "First Touch"}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Abbrechen"
          className="text-text-tertiary hover:text-text-primary"
        >
          <X size={14} />
        </button>
      </div>
      <p className="text-xs text-text-tertiary">
        Zeitpunkt: {new Date(computed).toLocaleString("de-DE")}
      </p>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label="Quelle">
          <input
            type="text"
            autoFocus
            value={utmSource}
            onChange={(e) => setUtmSource(e.target.value)}
            placeholder="z.B. facebook"
            className={FIELD_CLASS}
          />
        </Field>
        <Field label="Medium">
          <input
            type="text"
            value={utmMedium}
            onChange={(e) => setUtmMedium(e.target.value)}
            placeholder="z.B. cpc"
            className={FIELD_CLASS}
          />
        </Field>
      </div>

      <MetaCampaignFields
        projectId={projectId}
        value={campaignAttribution}
        onChange={setCampaignAttribution}
        portalContainer={dialogContainer}
      />

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-bg-hover"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className="inline-flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          {add.isPending && <Loader2 size={13} className="animate-spin" />}
          <Plus size={14} /> Einfügen
        </button>
      </div>
    </div>
  );
}
